import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"
import Alert from "../../components/ui/alert/Alert"
import { LoadState } from "@/components/ui/LoadState"

type Seedbed = { id: string; name: string }
type SeedbedGroup = { id: string; name: string; groupType: "individual" | "shared" | "team" }
type StudentSummary = { id: string; name: string; email: string }

export default function StudentEnrollPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const studentId = searchParams.get("studentId")
  const pendingId = searchParams.get("pendingId")
  const pendingName = searchParams.get("name")
  const pendingEmail = searchParams.get("email")

  const [student, setStudent] = useState<StudentSummary | null>(null)
  const [loadingStudent, setLoadingStudent] = useState(Boolean(studentId))
  const [loadError, setLoadError] = useState("")

  const [seedbeds, setSeedbeds] = useState<Seedbed[]>([])
  const [groups, setGroups] = useState<SeedbedGroup[]>([])
  const [seedbedId, setSeedbedId] = useState("")
  const [groupId, setGroupId] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api
      .get("/academy/seedbeds/admin?limit=100")
      .then((res) => setSeedbeds(res.data.data?.seedbeds ?? []))
      .catch(() => setSeedbeds([]))
  }, [])

  useEffect(() => {
    if (!studentId) return
    setLoadingStudent(true)
    api
      .get(`/admin/users/${studentId}`)
      .then((res) => {
        const data = res.data.data
        setStudent(data ? { id: data.id, name: data.name, email: data.email } : null)
      })
      .catch((err) => setLoadError(getErrorMessage(err)))
      .finally(() => setLoadingStudent(false))
  }, [studentId])

  useEffect(() => {
    if (!seedbedId) {
      setGroups([])
      return
    }
    api
      .get(`/academy/seedbeds/admin/${seedbedId}/groups`)
      .then((res) => setGroups(res.data.data ?? []))
      .catch(() => setGroups([]))
  }, [seedbedId])

  function goBack() {
    navigate("/instructor/students")
  }

  function targetIdentity(): { name: string; email: string } | null {
    if (studentId) return student ? { name: student.name, email: student.email } : null
    if (pendingId) return pendingName && pendingEmail ? { name: pendingName, email: pendingEmail } : null
    return null
  }

  async function handleSubmit() {
    const target = targetIdentity()
    if (!target || !seedbedId) return

    setSaving(true)
    try {
      // El endpoint ya resuelve "cuenta existente → inscribir de inmediato"
      // vs "sin cuenta (reserva pendiente) → reservar".
      await api.post(`/academy/seedbeds/admin/${seedbedId}/pending-members`, {
        name: target.name,
        email: target.email,
        group_id: groupId || undefined,
      })

      toastSuccess(`${target.name} quedó inscrita en el semillero.`)
      navigate("/instructor/students")
    } catch (err) {
      toastError(err)
    } finally {
      setSaving(false)
    }
  }

  const target = targetIdentity()
  const canSubmit = Boolean(target && seedbedId && !saving)
  const inputCls = "h-10 w-full rounded-lg border border-gray-300 px-3 text-sm dark:border-gray-700 dark:bg-gray-900"

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Docentes",
        { label: "Estudiantes", to: "/instructor/students" },
        "Inscribir en semillero",
      ]}
      title="Inscribir en semillero"
      subtitle="Asigna una cuenta existente o una reserva pendiente a un semillero"
      tabs={[]}
      metrics={[]}
    >
      {loadError ? (
        <Alert variant="error" title="No se pudo cargar el estudiante" message={loadError} />
      ) : (
        <div className="max-w-md space-y-4 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          {loadingStudent ? (
            <LoadState />
          ) : target ? (
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{target.name}</p>
              <p className="text-xs text-gray-500">{target.email}</p>
            </div>
          ) : (
            <Alert
              variant="error"
              title="Falta seleccionar a quién inscribir"
              message="Abre esta página desde una cuenta existente o una reserva pendiente en el listado de estudiantes."
            />
          )}

          <div>
            <label className="mb-1 block text-xs text-gray-500">Semillero *</label>
            <select value={seedbedId} onChange={(e) => setSeedbedId(e.target.value)} className={inputCls}>
              <option value="">Selecciona un semillero...</option>
              {seedbeds.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {groups.length > 0 && (
            <div>
              <label className="mb-1 block text-xs text-gray-500">Grupo (opcional)</label>
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={inputCls}>
                <option value="">Sin grupo</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} {g.groupType === "shared" ? "(cuenta grupal)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
            <button type="button" onClick={goBack} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800">
              Cancelar
            </button>
            <button type="button" disabled={!canSubmit} onClick={handleSubmit} className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
              {saving ? "Inscribiendo..." : "Inscribir"}
            </button>
          </div>
        </div>
      )}
    </ModuleLayout>
  )
}
